import React, { useState, useRef, useEffect, useMemo } from "react";
import { TopNavbar } from "@/components/layout";
import {
  TbClipboardList,
  TbCheck,
  TbClock,
  TbUsers,
  TbAlertTriangle,
  TbSearch,
  TbChevronDown,
} from "react-icons/tb";
import * as Select from "@radix-ui/react-select";
import {
  Map as MapLibreMap,
  Popup,
  type StyleSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "@/components/ui/select.css";
import { useUserTasks } from "@/hooks/useTasks";
import { useAllUsers } from "@/hooks/useUsers";
import type { Task } from "@/types/task";
import { bbox } from "@turf/turf";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";
import type { ChartEvent, ActiveElement, Chart, TooltipItem } from "chart.js";
import { Bar, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

export const Dashboard: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<MapLibreMap | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterAsset, setFilterAsset] = useState("all");
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);
  const [selectedAssignee, setSelectedAssignee] = useState<string | null>(null);
  const [selectedTask, setSelectedTask] = useState<string | null>(null);
  const [assigneeSearch, setAssigneeSearch] = useState("");

  // Fetch real data using hooks
  const { data: tasks = [] } = useUserTasks();
  const { data: users = [] } = useAllUsers();

  // Process and analyze the real data - updated to use filtered data
  const dashboardData = useMemo(() => {
    // Get filtered tasks first
    const currentFilteredTasks = tasks.filter((task) => {
      const user = users.find((u) => u.id === task.assignee);
      const assigneeName = user
        ? `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
          user.email
        : "Unknown User";

      const matchesSearch =
        task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        assigneeName.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus =
        filterStatus === "all" || task.status?.value === filterStatus;
      const matchesAssignee =
        filterAssignee === "all" || task.assignee === filterAssignee;
      const matchesAssetFilter =
        filterAsset === "all" || task.layer_name === filterAsset;
      const matchesAsset = !selectedAsset || task.layer_name === selectedAsset;
      const matchesSelectedAssignee =
        !selectedAssignee || task.assignee === selectedAssignee;
      const matchesSelectedTask = !selectedTask || task.id === selectedTask;

      return (
        matchesSearch &&
        matchesStatus &&
        matchesAssignee &&
        matchesAssetFilter &&
        matchesAsset &&
        matchesSelectedAssignee &&
        matchesSelectedTask
      );
    });

    if (!currentFilteredTasks.length && !tasks.length) {
      return {
        taskStats: {
          total: 0,
          open: 0,
          overdue: 0,
          active: 0,
          pending: 0,
          onHold: 0,
        },
        tasksByAsset: [],
        tasksByAssignee: [],
        completionStats: {
          totalCompleted: 0,
          completedThisWeek: 0,
          completedToday: 0,
          completionPercentage: 0,
        },
        tasks: currentFilteredTasks,
      };
    }

    // Calculate task statistics based on filtered data
    const total = currentFilteredTasks.length;
    const completed = currentFilteredTasks.filter(
      (task) => task.status?.value === "completed" || task.completed_at !== null
    ).length;
    const active = currentFilteredTasks.filter(
      (task) =>
        task.status?.value === "active" || task.status?.value === "in_progress"
    ).length;
    const pending = currentFilteredTasks.filter(
      (task) => task.status?.value === "pending"
    ).length;
    const onHold = currentFilteredTasks.filter(
      (task) => task.status?.value === "on_hold"
    ).length;

    // Calculate overdue tasks (due date passed and not completed) from filtered data
    const now = new Date();
    const overdue = currentFilteredTasks.filter((task) => {
      if (
        !task.due_date ||
        task.completed_at ||
        task.status?.value === "completed"
      )
        return false;
      return new Date(task.due_date) < now;
    }).length;

    const open = total - completed;

    // Group filtered tasks by asset/layer
    const assetGroups = currentFilteredTasks.reduce(
      (acc, task) => {
        const asset = task.layer_name || "Other";
        if (!acc[asset]) acc[asset] = [];
        acc[asset].push(task);
        return acc;
      },
      {} as Record<string, Task[]>
    );

    const tasksByAsset = Object.entries(assetGroups).map(
      ([name, taskList], index) => ({
        name,
        count: taskList.length,
        layer: name.toLowerCase(),
        color: [
          "#3b82f6",
          "#10b981",
          "#f59e0b",
          "#ef4444",
          "#8b5cf6",
          "#06b6d4",
        ][index % 6],
      })
    );

    // Group filtered tasks by assignee
    const assigneeGroups = currentFilteredTasks.reduce(
      (acc, task) => {
        if (task.assignee) {
          if (!acc[task.assignee]) acc[task.assignee] = [];
          acc[task.assignee].push(task);
        }
        return acc;
      },
      {} as Record<string, Task[]>
    );

    const tasksByAssignee = Object.entries(assigneeGroups).map(
      ([userId, taskList], index) => {
        const user = users.find((u) => u.id === userId);
        const activeTasks = taskList.filter(
          (task) =>
            task.status?.value === "active" ||
            task.status?.value === "in_progress"
        ).length;

        return {
          userId, // Add userId to the data for click handling
          name: user
            ? `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
              user.email
            : "Unknown User",
          count: taskList.length,
          active: activeTasks,
          color: [
            "#8b5cf6",
            "#06b6d4",
            "#84cc16",
            "#f97316",
            "#ec4899",
            "#3b82f6",
          ][index % 6],
        };
      }
    );

    // Calculate completion stats from filtered data
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    weekAgo.setHours(0, 0, 0, 0);

    const completedToday = currentFilteredTasks.filter((task) => {
      if (!task.completed_at) return false;
      const completedDate = new Date(task.completed_at);
      completedDate.setHours(0, 0, 0, 0);
      return completedDate.getTime() === today.getTime();
    }).length;

    const completedThisWeek = currentFilteredTasks.filter((task) => {
      if (!task.completed_at) return false;
      const completedDate = new Date(task.completed_at);
      return completedDate >= weekAgo;
    }).length;

    const completionPercentage =
      total > 0 ? Math.round((completed / total) * 100) : 0;

    return {
      taskStats: { total, open, overdue, active, pending, onHold },
      tasksByAsset,
      tasksByAssignee,
      completionStats: {
        totalCompleted: completed,
        completedThisWeek,
        completedToday,
        completionPercentage,
      },
      tasks: currentFilteredTasks,
    };
  }, [
    tasks,
    users,
    searchTerm,
    filterStatus,
    filterAssignee,
    filterAsset,
    selectedAsset,
    selectedAssignee,
    selectedTask,
  ]);

  // Use filtered tasks from dashboardData
  const filteredTasks = dashboardData.tasks;

  // Filter users based on search term
  const filteredUsers = users.filter((user) => {
    const userName =
      `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email;
    return userName.toLowerCase().includes(assigneeSearch.toLowerCase());
  });

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const mapStyle: StyleSpecification = {
        version: 8,
        sources: {
          osm: {
            type: "raster",
            tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
            tileSize: 256,
            attribution: "© OpenStreetMap contributors",
          },
        },
        layers: [
          {
            id: "osm",
            type: "raster",
            source: "osm",
          },
        ],
      };

      mapInstanceRef.current = new MapLibreMap({
        container: mapRef.current,
        style: mapStyle,
        center: [-74.006, 40.7128], // NYC coordinates [lng, lat]
        zoom: 11,
        attributionControl: false,
      });
    }
  }, []);

  // Update map with filtered tasks
  useEffect(() => {
    if (mapInstanceRef.current && filteredTasks) {
      const map = mapInstanceRef.current;

      // Wait for map to be loaded
      if (!map.loaded()) {
        map.once("load", () => {
          updateMapLayers();
        });
      } else {
        updateMapLayers();
      }

      function updateMapLayers() {
        if (!mapInstanceRef.current) return;
        const map = mapInstanceRef.current;

        // Remove existing task layers
        [
          "tasks-points",
          "tasks-lines",
          "tasks-polygons",
          "tasks-polygon-outlines",
        ].forEach((layerId) => {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        });
        if (map.getSource("tasks")) {
          map.removeSource("tasks");
        }

        // Get status color helper
        const getStatusColor = (status: string) => {
          switch (status) {
            case "active":
            case "in_progress":
              return "#10b981"; // green
            case "pending":
              return "#f59e0b"; // yellow
            case "todo":
              return "#f97316"; // orange
            case "on_hold":
              return "#6b7280"; // gray
            case "completed":
              return "#3b82f6"; // blue
            default:
              return "#ef4444"; // red
          }
        };

        // Create GeoJSON from filtered tasks
        const features = filteredTasks
          .filter((task) => task.geom)
          .map((task) => {
            const user = users.find((u) => u.id === task.assignee);
            const assigneeName = user
              ? `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                user.email
              : "Unassigned";

            return {
              type: "Feature" as const,
              geometry: task.geom!,
              properties: {
                id: task.id,
                title: task.title,
                assignee: assigneeName,
                status: task.status?.label || "Unknown",
                statusValue: task.status?.value || "",
                layerName: task.layer_name || "Unknown",
                dueDate: task.due_date
                  ? new Date(task.due_date).toLocaleDateString()
                  : "No due date",
                color: getStatusColor(task.status?.value || ""),
              },
            };
          });

        if (features.length === 0) return;

        const geojson: GeoJSON.FeatureCollection = {
          type: "FeatureCollection",
          features,
        };

        // Add source
        map.addSource("tasks", {
          type: "geojson",
          data: geojson,
        });

        // Add layers for different geometry types
        // Polygons fill
        map.addLayer({
          id: "tasks-polygons",
          type: "fill",
          source: "tasks",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "fill-color": ["get", "color"],
            "fill-opacity": 0.3,
          },
        });

        // Polygon outlines
        map.addLayer({
          id: "tasks-polygon-outlines",
          type: "line",
          source: "tasks",
          filter: ["==", ["geometry-type"], "Polygon"],
          paint: {
            "line-color": ["get", "color"],
            "line-width": 2,
            "line-opacity": 0.8,
          },
        });

        // Lines
        map.addLayer({
          id: "tasks-lines",
          type: "line",
          source: "tasks",
          filter: ["==", ["geometry-type"], "LineString"],
          paint: {
            "line-color": ["get", "color"],
            "line-width": 4,
            "line-opacity": 0.8,
          },
        });

        // Points
        map.addLayer({
          id: "tasks-points",
          type: "circle",
          source: "tasks",
          filter: ["==", ["geometry-type"], "Point"],
          paint: {
            "circle-radius": 8,
            "circle-color": ["get", "color"],
            "circle-stroke-color": ["get", "color"],
            "circle-stroke-width": 2,
            "circle-opacity": 0.6,
            "circle-stroke-opacity": 0.8,
          },
        });

        // Add click handlers for popups
        const popup = new Popup({
          closeButton: true,
          closeOnClick: false,
        });

        ["tasks-points", "tasks-lines", "tasks-polygons"].forEach((layerId) => {
          map.on("click", layerId, (e) => {
            if (!e.features || e.features.length === 0) return;

            const feature = e.features[0];
            const props = feature.properties;

            const popupContent = `
              <div class="p-2">
                <h3 class="font-bold text-sm">${props.title}</h3>
                <p class="text-xs text-gray-600">ID: ${props.id}</p>
                <p class="text-xs text-gray-600">Assignee: ${props.assignee}</p>
                <p class="text-xs text-gray-600">Status: ${props.status}</p>
                <p class="text-xs text-gray-600">Asset: ${props.layerName}</p>
                <p class="text-xs text-gray-600">Due: ${props.dueDate}</p>
              </div>
            `;

            popup.setLngLat(e.lngLat).setHTML(popupContent).addTo(map);
          });

          // Change cursor on hover
          map.on("mouseenter", layerId, () => {
            map.getCanvas().style.cursor = "pointer";
          });

          map.on("mouseleave", layerId, () => {
            map.getCanvas().style.cursor = "";
          });
        });

        // Fit map bounds to show all tasks
        try {
          const [minLng, minLat, maxLng, maxLat] = bbox(geojson);
          map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat],
            ],
            {
              padding: 20,
              maxZoom: 16,
            }
          );
        } catch (error) {
          console.warn("Error fitting bounds:", error);
        }
      }
    }
  }, [
    filteredTasks,
    users,
    searchTerm,
    filterStatus,
    filterAssignee,
    filterAsset,
    selectedAsset,
    selectedAssignee,
    selectedTask,
  ]);

  // Chart configurations and data (based on filtered tasks from dashboardData)
  const assetChartData = useMemo(() => {
    return {
      labels: dashboardData.tasksByAsset.map((asset) => asset.name),
      datasets: [
        {
          label: "Tasks by Asset Type",
          data: dashboardData.tasksByAsset.map((asset) => asset.count),
          backgroundColor: dashboardData.tasksByAsset.map(
            (asset) => asset.color
          ),
          borderColor: dashboardData.tasksByAsset.map((asset) => asset.color),
          borderWidth: 1,
        },
      ],
    };
  }, [dashboardData.tasksByAsset]);

  const assigneeChartData = useMemo(() => {
    return {
      labels: dashboardData.tasksByAssignee.map((assignee) => assignee.name),
      tasksByAssignee: dashboardData.tasksByAssignee, // Store the full data array for click handling
      datasets: [
        {
          label: "Total Tasks",
          data: dashboardData.tasksByAssignee.map((assignee) => assignee.count),
          backgroundColor: dashboardData.tasksByAssignee.map(
            (assignee) => assignee.color + "80"
          ), // Semi-transparent
          borderColor: dashboardData.tasksByAssignee.map(
            (assignee) => assignee.color
          ),
          borderWidth: 1,
        },
        {
          label: "Active Tasks",
          data: dashboardData.tasksByAssignee.map(
            (assignee) => assignee.active
          ),
          backgroundColor: dashboardData.tasksByAssignee.map(
            (assignee) => assignee.color
          ),
          borderColor: dashboardData.tasksByAssignee.map(
            (assignee) => assignee.color
          ),
          borderWidth: 1,
        },
      ],
    };
  }, [dashboardData.tasksByAssignee]);

  const completionChartData = useMemo(
    () => ({
      labels: ["Completed", "Remaining"],
      datasets: [
        {
          data: [
            dashboardData.completionStats.totalCompleted,
            dashboardData.taskStats.total -
              dashboardData.completionStats.totalCompleted,
          ],
          backgroundColor: ["#10b981", "#e5e7eb"],
          borderColor: ["#059669", "#d1d5db"],
          borderWidth: 2,
        },
      ],
    }),
    [
      dashboardData.completionStats.totalCompleted,
      dashboardData.taskStats.total,
    ]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"bar">) {
            const value =
              typeof context.parsed === "number"
                ? context.parsed
                : (context.parsed.y ?? context.parsed.x ?? 0);
            return `${context.dataset.label || ""}: ${value} tasks`;
          },
        },
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
    onClick: (_event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        if (chart.canvas.id === "asset-chart") {
          const assetName = assetChartData.labels?.[index];
          if (assetName) handleAssetClick(assetName as string);
        } else if (chart.canvas.id === "assignee-chart") {
          const assigneeName = assigneeChartData.labels?.[index];
          if (assigneeName) handleAssigneeClick(assigneeName as string);
        }
      }
    },
  };

  const horizontalChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y" as const,
    plugins: {
      legend: {
        position: "top" as const,
      },
      tooltip: {
        callbacks: {
          label: function (context: TooltipItem<"bar">) {
            const value =
              typeof context.parsed === "number"
                ? context.parsed
                : (context.parsed.y ?? context.parsed.x ?? 0);
            return `${context.dataset.label || ""}: ${value} tasks`;
          },
        },
      },
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          stepSize: 1,
        },
      },
    },
    onClick: (_event: ChartEvent, elements: ActiveElement[]) => {
      if (elements.length > 0) {
        const index = elements[0].index;
        if (
          assigneeChartData.tasksByAssignee &&
          assigneeChartData.tasksByAssignee[index]
        ) {
          const userId = assigneeChartData.tasksByAssignee[index].userId;
          if (userId) handleAssigneeClick(userId);
        }
      }
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
      },
      tooltip: {
        callbacks: {
          label: function (context: {
            label?: string;
            parsed: number;
            dataset: { data: number[] };
          }) {
            const total = context.dataset.data.reduce(
              (a: number, b: number) => a + b,
              0
            );
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label || ""}: ${
              context.parsed
            } tasks (${percentage}%)`;
          },
        },
      },
    },
  };

  const handleAssetClick = (assetName: string) => {
    setSelectedAsset(selectedAsset === assetName ? null : assetName);
  };

  const handleAssigneeClick = (userId: string) => {
    setSelectedAssignee(selectedAssignee === userId ? null : userId);
  };

  const handleTaskClick = (taskId: string) => {
    setSelectedTask(selectedTask === taskId ? null : taskId);
  };

  const handleAssigneeFilterChange = (value: string) => {
    setFilterAssignee(value);
    setAssigneeSearch(""); // Clear search when selection changes
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-0 w-full">
        <TopNavbar className="" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          {/* Header */}
          <div className="mb-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <h1 className="text-2xl font-bold text-custom-text-onyx">
                Dashboard
              </h1>

              {/* Filters Row */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="relative">
                  <TbSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search tasks..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="bg-white pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[200px]"
                  />
                </div>

                <Select.Root
                  value={filterStatus}
                  onValueChange={setFilterStatus}
                >
                  <Select.Trigger className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[120px] bg-white">
                    <Select.Value placeholder="All Status" />
                    <Select.Icon>
                      <TbChevronDown className="w-4 h-4" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="bg-white border border-gray-300 rounded-lg shadow-lg z-50 min-w-[120px]">
                      <Select.Viewport className="p-1">
                        <Select.Item
                          value="all"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>All Status</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="completed"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>Completed</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="cancelled"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>Cancelled</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="in_review"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>In Review</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="in_progress"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>In Progress</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="pending"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>Pending</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="on_hold"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>On Hold</Select.ItemText>
                        </Select.Item>
                        <Select.Item
                          value="todo"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>To Do</Select.ItemText>
                        </Select.Item>
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>

                <Select.Root
                  value={filterAssignee}
                  onValueChange={handleAssigneeFilterChange}
                >
                  <Select.Trigger className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[140px] bg-white data-placeholder:text-gray-500">
                    <Select.Value placeholder="All Assignees">
                      {filterAssignee === "all"
                        ? "All Assignees"
                        : users.find((u) => u.id === filterAssignee)
                          ? `${
                              users.find((u) => u.id === filterAssignee)
                                ?.first_name || ""
                            } ${
                              users.find((u) => u.id === filterAssignee)
                                ?.last_name || ""
                            }`.trim() ||
                            users.find((u) => u.id === filterAssignee)?.email
                          : "All Assignees"}
                    </Select.Value>
                    <Select.Icon>
                      <TbChevronDown className="w-4 h-4 text-gray-400" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content
                      className="bg-white border border-gray-300 rounded-lg shadow-lg z-9999 min-w-[140px] overflow-hidden"
                      position="popper"
                      sideOffset={4}
                    >
                      <div className="p-2 border-b border-gray-200 bg-gray-50">
                        <div className="relative">
                          <TbSearch className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-3 h-3 pointer-events-none" />
                          <input
                            type="text"
                            placeholder="Search assignees..."
                            value={assigneeSearch}
                            onChange={(e) => setAssigneeSearch(e.target.value)}
                            className="w-full pl-6 pr-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => e.stopPropagation()}
                          />
                        </div>
                      </div>
                      <Select.Viewport className="p-1 max-h-[200px] overflow-y-auto">
                        <Select.Item
                          value="all"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded outline-none focus:bg-gray-100 data-highlighted:bg-gray-100"
                        >
                          <Select.ItemText>All Assignees</Select.ItemText>
                        </Select.Item>
                        {filteredUsers.map((user) => (
                          <Select.Item
                            key={user.id}
                            value={user.id}
                            className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded outline-none focus:bg-gray-100 data-highlighted:bg-gray-100"
                          >
                            <Select.ItemText>
                              {`${user.first_name || ""} ${
                                user.last_name || ""
                              }`.trim() || user.email}
                            </Select.ItemText>
                          </Select.Item>
                        ))}
                        {filteredUsers.length === 0 && assigneeSearch && (
                          <div className="px-3 py-2 text-sm text-gray-500 italic">
                            No assignees found
                          </div>
                        )}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>

                <Select.Root value={filterAsset} onValueChange={setFilterAsset}>
                  <Select.Trigger className="flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[130px] bg-white">
                    <Select.Value placeholder="All Assets" />
                    <Select.Icon>
                      <TbChevronDown className="w-4 h-4" />
                    </Select.Icon>
                  </Select.Trigger>
                  <Select.Portal>
                    <Select.Content className="bg-white border border-gray-300 rounded-lg shadow-lg z-50 min-w-[130px] max-h-[200px] overflow-y-auto">
                      <Select.Viewport className="p-1">
                        <Select.Item
                          value="all"
                          className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                        >
                          <Select.ItemText>All Assets</Select.ItemText>
                        </Select.Item>
                        {dashboardData.tasksByAsset.map((asset) => (
                          <Select.Item
                            key={asset.name}
                            value={asset.name}
                            className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 rounded"
                          >
                            <Select.ItemText>{asset.name}</Select.ItemText>
                          </Select.Item>
                        ))}
                      </Select.Viewport>
                    </Select.Content>
                  </Select.Portal>
                </Select.Root>
              </div>
            </div>
          </div>
          {/* Stats Cards Row */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-custom-text-gray-dark">
                    Total
                  </p>
                  <p className="text-xl font-bold text-custom-text-onyx">
                    {dashboardData.taskStats.total}
                  </p>
                </div>
                <TbClipboardList className="h-6 w-6 text-blue-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-custom-text-gray-dark">
                    Open
                  </p>
                  <p className="text-xl font-bold text-custom-text-onyx">
                    {dashboardData.taskStats.open}
                  </p>
                </div>
                <TbClock className="h-6 w-6 text-blue-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-custom-text-gray-dark">
                    Overdue
                  </p>
                  <p className="text-xl font-bold text-red-600">
                    {dashboardData.taskStats.overdue}
                  </p>
                </div>
                <TbAlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-custom-text-gray-dark">
                    Active
                  </p>
                  <p className="text-xl font-bold text-green-600">
                    {dashboardData.taskStats.active}
                  </p>
                </div>
                <TbCheck className="h-6 w-6 text-green-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-custom-text-gray-dark">
                    Pending
                  </p>
                  <p className="text-xl font-bold text-yellow-600">
                    {dashboardData.taskStats.pending}
                  </p>
                </div>
                <TbClock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-custom-text-gray-dark">
                    On Hold
                  </p>
                  <p className="text-xl font-bold text-gray-600">
                    {dashboardData.taskStats.onHold}
                  </p>
                </div>
                <TbUsers className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </div>

          {/* Completion Stats and Map Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
            {/* Completion Stats with Pie Chart */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold text-custom-text-onyx mb-3">
                Completion Stats
              </h2>

              {/* Doughnut Chart */}
              <div className="flex items-center justify-center mb-4">
                <div className="w-32 h-32">
                  <Doughnut
                    data={completionChartData}
                    options={doughnutOptions}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-xs text-custom-text-gray-dark">
                    Total Completed
                  </span>
                  <span className="font-medium text-custom-text-onyx text-sm">
                    {dashboardData.completionStats.totalCompleted}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-custom-text-gray-dark">
                    This Week
                  </span>
                  <span className="font-medium text-custom-text-onyx text-sm">
                    {dashboardData.completionStats.completedThisWeek}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-xs text-custom-text-gray-dark">
                    Today
                  </span>
                  <span className="font-medium text-custom-text-onyx text-sm">
                    {dashboardData.completionStats.completedToday}
                  </span>
                </div>
              </div>
            </div>

            {/* Interactive Map */}
            <div className="lg:col-span-2 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-base font-semibold text-custom-text-onyx mb-3">
                Task Locations
              </h2>
              <div ref={mapRef} className="w-full h-56 rounded-lg z-2"></div>
            </div>
          </div>
          {/* Charts and Task List Row */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
            <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
              {/* Charts Section - Left Side */}
              <div className="xl:col-span-2">
                <div className="space-y-6">
                  {/* Tasks by Asset Type Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-custom-text-onyx mb-3">
                      Tasks by Asset Type
                    </h3>
                    <div className="h-48">
                      <Bar
                        id="asset-chart"
                        data={assetChartData}
                        options={chartOptions}
                      />
                    </div>
                    {/* Asset Filter Message */}
                    {selectedAsset && (
                      <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-800">
                          Filtered by: <strong>{selectedAsset}</strong>
                          <button
                            onClick={() => setSelectedAsset(null)}
                            className="cursor-pointer ml-2 text-blue-600 hover:text-blue-800 text-xs"
                          >
                            Clear filter
                          </button>
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Tasks by Assignee Section */}
                  <div>
                    <h3 className="text-sm font-semibold text-custom-text-onyx mb-3">
                      Tasks by Assignee
                    </h3>
                    <div className="h-48">
                      <Bar
                        id="assignee-chart"
                        data={assigneeChartData}
                        options={horizontalChartOptions}
                      />
                    </div>
                    {/* Assignee Filter Message */}
                    {selectedAssignee && (
                      <div className="mt-3 p-2 bg-blue-50 rounded-lg">
                        <p className="text-xs text-blue-800">
                          Filtered by:{" "}
                          <strong>
                            {(() => {
                              const user = users.find(
                                (u) => u.id === selectedAssignee
                              );
                              return user
                                ? `${user.first_name || ""} ${
                                    user.last_name || ""
                                  }`.trim() || user.email
                                : "Unknown User";
                            })()}
                          </strong>
                          <button
                            onClick={() => setSelectedAssignee(null)}
                            className="cursor-pointer ml-2 text-blue-600 hover:text-blue-800 text-xs"
                          >
                            Clear filter
                          </button>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Task List Section - Right Side */}
              <div className="xl:col-span-1">
                <div className="mb-4">
                  <h3 className="text-sm font-semibold text-custom-text-onyx mb-3">
                    Tasks List
                  </h3>
                  {/* Selected Task Filter Message */}
                  {selectedTask && (
                    <div className="mb-3 p-2 bg-purple-50 rounded-lg">
                      <p className="text-xs text-purple-800">
                        Filtered by task:{" "}
                        <strong>
                          {dashboardData.tasks.find(
                            (t) => t.id === selectedTask
                          )?.title || "Unknown Task"}
                        </strong>
                        <button
                          onClick={() => setSelectedTask(null)}
                          className="cursor-pointer ml-2 text-purple-600 hover:text-purple-800 text-xs"
                        >
                          Clear filter
                        </button>
                      </p>
                    </div>
                  )}
                </div>

                {/* Task List Content */}
                <div className="space-y-4 max-h-96 overflow-y-auto">
                  {/* Active Tasks */}
                  {filteredTasks.filter(
                    (task) =>
                      task.status?.value === "active" ||
                      task.status?.value === "in_progress"
                  ).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-green-600 mb-2">
                        Active (
                        {
                          filteredTasks.filter(
                            (task) =>
                              task.status?.value === "active" ||
                              task.status?.value === "in_progress"
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-1">
                        {filteredTasks
                          .filter(
                            (task) =>
                              task.status?.value === "active" ||
                              task.status?.value === "in_progress"
                          )
                          .map((task) => (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className={`text-xs text-custom-text-onyx p-2 rounded border-l-2 border-green-500 cursor-pointer transition-colors ${
                                selectedTask === task.id
                                  ? "bg-green-200 border-green-700 shadow-md"
                                  : "bg-green-50 hover:bg-green-100"
                              }`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">
                                {task.title}
                              </div>
                              {selectedTask === task.id && (
                                <div className="text-xs text-green-700 mt-1">
                                  ✓ Selected - Filtering charts and map
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Pending Tasks */}
                  {filteredTasks.filter(
                    (task) => task.status?.value === "pending"
                  ).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-yellow-600 mb-2">
                        Pending (
                        {
                          filteredTasks.filter(
                            (task) => task.status?.value === "pending"
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-1">
                        {filteredTasks
                          .filter((task) => task.status?.value === "pending")
                          .map((task) => (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className={`text-xs text-custom-text-onyx p-2 rounded border-l-2 border-yellow-500 cursor-pointer transition-colors ${
                                selectedTask === task.id
                                  ? "bg-yellow-200 border-yellow-700 shadow-md"
                                  : "bg-yellow-50 hover:bg-yellow-100"
                              }`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">
                                {task.title}
                              </div>
                              {selectedTask === task.id && (
                                <div className="text-xs text-yellow-700 mt-1">
                                  ✓ Selected - Filtering charts and map
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* To Do Tasks */}
                  {filteredTasks.filter((task) => task.status?.value === "todo")
                    .length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-orange-600 mb-2">
                        To Do (
                        {
                          filteredTasks.filter(
                            (task) => task.status?.value === "todo"
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-1">
                        {filteredTasks
                          .filter((task) => task.status?.value === "todo")
                          .map((task) => (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className={`text-xs text-custom-text-onyx p-2 rounded border-l-2 border-orange-500 cursor-pointer transition-colors ${
                                selectedTask === task.id
                                  ? "bg-orange-200 border-orange-700 shadow-md"
                                  : "bg-orange-50 hover:bg-orange-100"
                              }`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">
                                {task.title}
                              </div>
                              {selectedTask === task.id && (
                                <div className="text-xs text-orange-700 mt-1">
                                  ✓ Selected - Filtering charts and map
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* On Hold Tasks */}
                  {filteredTasks.filter(
                    (task) => task.status?.value === "on_hold"
                  ).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-gray-600 mb-2">
                        On Hold (
                        {
                          filteredTasks.filter(
                            (task) => task.status?.value === "on_hold"
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-1">
                        {filteredTasks
                          .filter((task) => task.status?.value === "on_hold")
                          .map((task) => (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className={`text-xs text-custom-text-onyx p-2 rounded border-l-2 border-gray-500 cursor-pointer transition-colors ${
                                selectedTask === task.id
                                  ? "bg-gray-200 border-gray-700 shadow-md"
                                  : "bg-gray-50 hover:bg-gray-100"
                              }`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">
                                {task.title}
                              </div>
                              {selectedTask === task.id && (
                                <div className="text-xs text-gray-700 mt-1">
                                  ✓ Selected - Filtering charts and map
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Completed Tasks */}
                  {filteredTasks.filter(
                    (task) =>
                      task.status?.value === "completed" ||
                      task.completed_at !== null
                  ).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-blue-600 mb-2">
                        Completed (
                        {
                          filteredTasks.filter(
                            (task) =>
                              task.status?.value === "completed" ||
                              task.completed_at !== null
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-1">
                        {filteredTasks
                          .filter(
                            (task) =>
                              task.status?.value === "completed" ||
                              task.completed_at !== null
                          )
                          .map((task) => (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className={`text-xs text-custom-text-onyx p-2 rounded border-l-2 border-blue-500 cursor-pointer transition-colors ${
                                selectedTask === task.id
                                  ? "bg-blue-200 border-blue-700 shadow-md"
                                  : "bg-blue-50 hover:bg-blue-100"
                              }`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">
                                {task.title}
                              </div>
                              {selectedTask === task.id && (
                                <div className="text-xs text-blue-700 mt-1">
                                  ✓ Selected - Filtering charts and map
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {/* Other Status Tasks (any status not covered above) */}
                  {filteredTasks.filter(
                    (task) =>
                      task.status?.value !== "active" &&
                      task.status?.value !== "in_progress" &&
                      task.status?.value !== "cancelled" &&
                      task.status?.value !== "in_review" &&
                      task.status?.value !== "pending" &&
                      task.status?.value !== "todo" &&
                      task.status?.value !== "on_hold" &&
                      task.status?.value !== "completed" &&
                      task.completed_at === null
                  ).length > 0 && (
                    <div>
                      <h4 className="text-xs font-semibold text-purple-600 mb-2">
                        Other (
                        {
                          filteredTasks.filter(
                            (task) =>
                              task.status?.value !== "active" &&
                              task.status?.value !== "in_progress" &&
                              task.status?.value !== "cancelled" &&
                              task.status?.value !== "in_review" &&
                              task.status?.value !== "pending" &&
                              task.status?.value !== "todo" &&
                              task.status?.value !== "on_hold" &&
                              task.status?.value !== "completed" &&
                              task.completed_at === null
                          ).length
                        }
                        )
                      </h4>
                      <div className="space-y-1">
                        {filteredTasks
                          .filter(
                            (task) =>
                              task.status?.value !== "active" &&
                              task.status?.value !== "in_progress" &&
                              task.status?.value !== "cancelled" &&
                              task.status?.value !== "in_review" &&
                              task.status?.value !== "pending" &&
                              task.status?.value !== "todo" &&
                              task.status?.value !== "on_hold" &&
                              task.status?.value !== "completed" &&
                              task.completed_at === null
                          )
                          .map((task) => (
                            <div
                              key={task.id}
                              onClick={() => handleTaskClick(task.id)}
                              className={`text-xs text-custom-text-onyx p-2 rounded border-l-2 border-purple-500 cursor-pointer transition-colors ${
                                selectedTask === task.id
                                  ? "bg-purple-200 border-purple-700 shadow-md"
                                  : "bg-purple-50 hover:bg-purple-100"
                              }`}
                              title={task.title}
                            >
                              <div className="font-medium truncate">
                                {task.title}
                                {task.status?.label && (
                                  <span className="ml-1 text-purple-600">
                                    ({task.status.label})
                                  </span>
                                )}
                              </div>
                              {selectedTask === task.id && (
                                <div className="text-xs text-purple-700 mt-1">
                                  ✓ Selected - Filtering charts and map
                                </div>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}

                  {filteredTasks.length === 0 && (
                    <div className="text-center py-4">
                      <p className="text-custom-text-gray-dark text-xs">
                        No tasks found matching your criteria.
                      </p>
                    </div>
                  )}

                  {filteredTasks.length > 0 &&
                    filteredTasks.filter(
                      (task) =>
                        task.status?.value === "active" ||
                        task.status?.value === "in_progress" ||
                        task.status?.value === "cancelled" ||
                        task.status?.value === "in_review" ||
                        task.status?.value === "pending" ||
                        task.status?.value === "todo" ||
                        task.status?.value === "on_hold" ||
                        task.status?.value === "completed" ||
                        task.completed_at !== null ||
                        (task.status?.value !== "active" &&
                          task.status?.value !== "in_progress" &&
                          task.status?.value !== "cancelled" &&
                          task.status?.value !== "in_review" &&
                          task.status?.value !== "pending" &&
                          task.status?.value !== "todo" &&
                          task.status?.value !== "on_hold" &&
                          task.status?.value !== "completed" &&
                          task.completed_at === null)
                    ).length === 0 && (
                      <div className="text-center py-4">
                        <p className="text-custom-text-gray-dark text-xs">
                          No tasks with recognized status found.
                        </p>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
